import { CodeEditor as AidKitCodeEditor } from "@aidkitorg/component-library";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { TrackedPrimitive } from "./collab";
import { NodeProps } from "./editor";
import { useDebounce } from "./utils";

export function TextArea(props: NodeProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [value, setValue] = useState(props.trackedObject!.value);
  const [inputValid, setInputValid] = useState(true);

  const [debouncedSetTrackedObject,] = useDebounce((value: string) => {
    (props.trackedObject! as TrackedPrimitive).set(value);
    props.trackedObject?.locateUserHere();
    props.onChange?.();
  }, 500);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleInputValueChange(e.target.value);
  }

  const handleInputValueChange = (value: string) => {
    setValue(value);
    debouncedSetTrackedObject(value);
  }

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();

      const textarea = e.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // Insert 4 spaces
      const newValue = value.slice(0, start) + '    ' + value.slice(end);

      setValue(newValue);
      debouncedSetTrackedObject(newValue);
      // Move cursor to after the inserted spaces
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 4;
      });
    }
  }, [value]);

  // Track if the value is valid
  useEffect(() => {
    setInputValid(() => {
      let valid = true;
      // These aren't mutually exclusive validators
      // Update valid to false if at least one of these fails
      if (props.node.meta?.validateJS) {
        if (value.trim() === '') {
          // valid;
        } else {
          const fnStr = props.node.meta.validateJS.replace('expr', value);
          try {
            const fn = new Function('info', `return ${fnStr}`);
            fn({});
            // valid
          } catch (e2) {
            // For debugging in dev
            // alert("Error:" + (e2 as any).toString());
            valid = false;
          }
        }
      }

      if (props.node.meta?.format) {
        const regex = new RegExp(props.node.meta.format);
        let formatValid = regex.test(value);
        if (!formatValid) valid = false;
      }

      return valid;
    })
  }, [value])

  useEffect(() => {
    const viewingAuthor = props.trackedObject?.state.currentAuthor;
    const editingAuthorsNotMe = (props.trackedObject?.state.objectToActivity?.[props.trackedObject?.id] || []).some(
      ea => (ea.uid !== viewingAuthor?.uid || ea.tabId !== viewingAuthor?.tabId));

    // this can cause cursor jumping issues if we are the one editing, and then our own changes get propogated through. 
    // we only really care for this useEffect when someone else is editing.
    if (editingAuthorsNotMe) {
      if (value !== props.trackedObject!.value) {
        setValue(props.trackedObject!.value);
      }
    }
  }, [props.trackedObject!.value, props.trackedObject?.state.objectToActivity]);

  const rows = props.node.meta?.rows;
  
  const componentClasses = props.node.meta?.component 
    ? props.node.meta.component.split(' ').slice(1).join(' ')
    : '';
  
  const textEditorClassName = `w-full ${!inputValid ? 'border-4 rounded border-red-400 ring-red-400' : ''} ${props.node.meta?.language ? '' : 'grow-wrap'}`;

  return (
    <div className={textEditorClassName} data-replicated-value={value}
      onClick={() => !!props.updateBreadcrumb && props.updateBreadcrumb(props.trackedObject)}>
      {
        props.node.meta?.language
          ? <AidKitCodeEditor
            value={value}
            onChange={handleInputValueChange}
            language={props.node.meta.language}
            readOnly={props.readonly}
          />
          : <textarea ref={textareaRef} className={`read-only:bg-transparent ${componentClasses}`}
            dir='auto'
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            readOnly={props.readonly}
            rows={rows}
          />
      }
      {!inputValid && <div className="ml-3 text-red-500 text-xs">Your input is invalid. {props.node.meta?.formatMessage || ''}</div>}
    </div>
  );
}
